import { createAsyncThunk } from '@reduxjs/toolkit';
import { getPVZListV2 } from '@/api/deliveryApi';
import { type PVZPointV2Optional } from '@/types/IFetchPvzList';
import { getErrorData, type IErrorData } from '@/utils/getErrorData';
import { type IFetchPvzListParams, type ISelectPvzParams } from '../types';
import { type RootState } from '../../store';
import { loadPvzFromCache } from '../index';
import {
  IUpdateOrderDeliveryPayload,
  IUpdateOrderDeliveryResponse
} from '@/types/IUpdateOrderDelivery';
import { updateOrderDelivery } from '@/hooks/orderOfferApi';
import { isError } from '@/utils/typeguards/isError';

// Интерфейс для возврата данных с кешированием
interface IFetchPvzListResult {
  pvzList: PVZPointV2Optional[];
  city: string;
  provider: string;
  type: string;
}

// thunk для кешированной загрузки ПВЗ
export const fetchPvzListWithCacheThunk = createAsyncThunk<
  IFetchPvzListResult,
  IFetchPvzListParams,
  { rejectValue: IErrorData }
>('deliveryEdit/fetchPvzListWithCache', async (params, { rejectWithValue }) => {
  try {
    const { city, provider, type } = params;

    if (city === '' || provider === '' || type === '') {
      return {
        pvzList: [],
        city,
        provider,
        type
      };
    }

    let operations = '2';
    if (type === 'delivery') {
      operations = '1,3';
    } else if (type === 'pickup') {
      operations = '2,3';
    }

    const pvzList = await getPVZListV2(provider, city, operations);

    if (!Array.isArray(pvzList)) {
      throw new Error('Полученные данные не являются массивом');
    }

    return {
      pvzList,
      city,
      provider,
      type
    };
  } catch (error) {
    return rejectWithValue(getErrorData(error));
  }
});

// Выбор ПВЗ (координирует обновление всех связанных состояний)
export const selectPvzThunk = createAsyncThunk<
  PVZPointV2Optional,
  ISelectPvzParams,
  { rejectValue: IErrorData }
>('deliveryEdit/selectPvz', async (params, { rejectWithValue }) => {
  try {
    const { point } = params;
    return point;
  } catch (error) {
    return rejectWithValue(getErrorData(error));
  }
});

// Умный thunk для загрузки ПВЗ с проверкой кеша
export const fetchPvzListSmartThunk = createAsyncThunk<
  IFetchPvzListResult,
  IFetchPvzListParams,
  { rejectValue: IErrorData; state: RootState }
>(
  'deliveryEdit/fetchPvzListSmart',
  async (params, { rejectWithValue, getState, dispatch }) => {
    try {
      const { city, provider, type } = params;

      if (city === '' || provider === '' || type === '') {
        return {
          pvzList: [],
          city,
          provider,
          type
        };
      }
      const cacheKey = `${city}_${provider}_${type}`;
      const state = getState();
      const cachedData = state.deliveryEdit.cache.pvzCache[cacheKey];

      if (cachedData !== null && cachedData !== undefined) {
        dispatch(loadPvzFromCache(cacheKey));
        return {
          pvzList: cachedData.pvzList,
          city: cachedData.city,
          provider: cachedData.provider,
          type: cachedData.type
        };
      }
      const result = await dispatch(fetchPvzListWithCacheThunk(params));

      if (fetchPvzListWithCacheThunk.fulfilled.match(result)) {
        return result.payload;
      } else {
        return rejectWithValue(getErrorData('Ошибка загрузки ПВЗ'));
      }
    } catch (error) {
      return rejectWithValue(getErrorData(error));
    }
  }
);

export const updateOrderDeliveryThunk = createAsyncThunk<
  IUpdateOrderDeliveryResponse,
  { orderUuid: string; payload: IUpdateOrderDeliveryPayload },
  { rejectValue: IErrorData }
>(
  'deliveryEdit/updateOrderDeliveryThunk',
  async ({ orderUuid, payload }, { rejectWithValue }) => {
    try {
      const response = await updateOrderDelivery(orderUuid, payload);

      if (isError(response)) {
        return rejectWithValue(response);
      }

      return response;
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);
